import { createRouter, createWebHistory } from 'vue-router'
import Dashboard from '@/views/DashboardView.vue'
import Login from '@/views/LoginView.vue'
import SpectraList from '@/views/spectra/SpectraListView.vue'
import ImportSpectra from '@/views/spectra/ImportSpectraView.vue'
import CollectionList from '@/views/collections/CollectionListView.vue'
import AddCollection from '@/views/collections/AddCollectionView.vue'
import TaskList from '@/views/tasks/TaskListView.vue'
import NewsList from '@/views/news/NewsListView.vue'
import UserList from '@/views/users/UserListView.vue'
import AddNews from '@/views/news/AddNewsView.vue'
import AddUser from '@/views/users/AddUserView.vue'
import RoleList from '@/views/roles/RoleListView.vue'
import AddRole from '@/views/roles/AddRoleView.vue'
import FileBrowser from '@/views/files/FileBrowserView.vue'
import ServerList from '@/views/servers/ServerListView.vue'
import AddServer from '@/views/servers/AddServerView.vue'
import VersionListView from '@/views/versions/VersionListView.vue'
import AddVersionView from '@/views/versions/AddVersionView.vue'
import ForgotPassword from '@/views/password/ForgotPasswordView.vue'
import ResetPassword from '@/views/password/ResetPasswordView.vue'
import UpdatePassword from '@/views/password/UpdatePasswordView.vue'
import { useStore } from '@/core/store'
import AxiosClient from '@/utils/AxiosClient'
import { flashMessage } from '@/utils/FlashMessages'

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: '/',
      name: 'dashboard',
      component: Dashboard,
      meta: {
        requiresAuth: true
      }
    },
    {
      path: '/login',
      name: 'login',
      component: Login,
      meta: {
        requiresAuth: false
      }
    },
    {
      path: '/spectra',
      name: 'spectra',
      component: SpectraList,
      meta: {
        requiresAuth: true
      }
    },
    {
      path: '/spectra/import',
      name: 'import-spectra',
      component: ImportSpectra,
      meta: {
        requiresAuth: true
      }
    },
    {
      path: '/collections',
      name: 'collections',
      component: CollectionList,
      meta: {
        requiresAuth: true
      }
    },
    {
      path: '/collections/new',
      name: 'new-collection',
      component: AddCollection,
      meta: {
        requiresAuth: true
      }
    },
    {
      path: '/collections/edit/:id',
      name: 'edit-collection',
      component: AddCollection,
      meta: {
        requiresAuth: true
      }
    },
    {
      path: '/tasks',
      name: 'tasks',
      component: TaskList,
      meta: {
        requiresAuth: true
      }
    },
    {
      path: '/news',
      name: 'news',
      component: NewsList,
      meta: {
        requiresAuth: true
      }
    },
    {
      path: '/news/new',
      name: 'new-article',
      component: AddNews,
      meta: {
        requiresAuth: true
      }
    },
    {
      path: '/news/edit/:id',
      name: 'edit-article',
      component: AddNews,
      meta: {
        requiresAuth: true
      }
    },
    {
      path: '/users',
      name: 'users',
      component: UserList,
      meta: {
        requiresAuth: true
      }
    },
    {
      path: '/users/new',
      name: 'new-user',
      component: AddUser,
      meta: {
        requiresAuth: true
      }
    },
    {
      path: '/users/edit/:id',
      name: 'edit-user',
      component: AddUser,
      meta: {
        requiresAuth: true
      }
    },
    {
      path: '/roles',
      name: 'roles',
      component: RoleList,
      meta: {
        requiresAuth: true
      }
    },
    {
      path: '/roles/new',
      name: 'new-role',
      component: AddRole,
      meta: {
        requiresAuth: true
      }
    },
    {
      path: '/roles/edit/:id',
      name: 'edit-role',
      component: AddRole,
      meta: {
        requiresAuth: true
      }
    },
    {
      path: '/files',
      name: 'files',
      component: FileBrowser,
      meta: {
        requiresAuth: true
      }
    },
    {
      path: '/servers',
      name: 'servers',
      component: ServerList,
      meta: {
        requiresAuth: true
      }
    },
    {
      path: '/servers/new',
      name: 'new-server',
      component: AddServer,
      meta: {
        requiresAuth: true
      }
    },
    {
      path: '/servers/edit/:id',
      name: 'edit-server',
      component: AddServer,
      meta: {
        requiresAuth: true
      }
    },
    {
      path: '/versions',
      name: 'versions',
      component: VersionListView,
      meta: {
        requiresAuth: true
      }
    },
    {
      path: '/versions/new',
      name: 'new-version',
      component: AddVersionView,
      meta: {
        requiresAuth: true
      }
    },
    {
      path: '/versions/edit/:id',
      name: 'edit-version',
      component: AddVersionView,
      meta: {
        requiresAuth: true
      }
    },
    {
      path: '/forgot-password',
      name: 'forgot-password',
      component: ForgotPassword,
      meta: {
        requiresAuth: false
      }
    },
    {
      path: '/reset-password',
      name: 'reset-password',
      component: ResetPassword,
      meta: {
        requiresAuth: false
      }
    },
    {
      path: '/update-password',
      name: 'update-password',
      component: UpdatePassword,
      meta: {
        requiresAuth: true
      }
    },
    {
      path: '/logout',
      name: 'logout',
      component: Login,
      meta: {
        forceLogout: true
      }
    }
  ]
})

router.beforeEach(async (to, from, next) => {
  const store = useStore()
  const client = new AxiosClient(import.meta.env.VITE_API_URL + '/api')
  const isLoggedIn = store.isAuthenticated && (await client.verifyToken(store.getAccessToken()))

  if (to.meta.forceLogout) {
    store.logout()
    next({ name: 'login' })
  } else if (to.meta.requiresAuth && !isLoggedIn) {
    flashMessage.error('You need to be logged in to access this page')
    next({ name: 'login' })
  } else if (!to.meta.requiresAuth && isLoggedIn) {
    next({ name: 'dashboard' })
  } else if (to.meta.requiresAuth && isLoggedIn) {
    next()
    store.refresh().catch(() => {
      flashMessage.error('Your session has expired, please log in again')
      store.logout()
      next({ name: 'login' })
    })
  } else {
    next()
  }
})

export default router
